/* eslint-disable react/prop-types */
import React from 'react';
import { Document, Page, View, Text, StyleSheet, Image, Font } from '@react-pdf/renderer';
import moment from 'moment';
import "moment-timezone";
import Html from 'react-pdf-html';
import DOMPurify from 'dompurify';

export const HEADER_HEIGHT = 76;
export const FOOTER_HEIGHT = 34;
const LOGO_HEIGHT = 46;

// Used in the footer when companyData has no email / website.
export const DEFAULT_FOOTER_CONTACT = 'hello@cxsquare.solutions  ·  www.cxsquare.solutions';

export const COLORS = {
    primary: '#407FE9',
    navy: '#272B41',
    text: '#272B41',
    heading: '#262626',
    secondary: '#5A5F7D',
    muted: '#ADB4D2',
    label: '#848484',
    app: '#F4F5F7',
    white: '#FFFFFF',
    tableHead: '#EBF7FF',
    border: '#CDCDCD',
    divider: '#F1F2F6',
};

/**
 * Colour themes. Only the coloured "shell" changes (header, footer, table header,
 * Grand Total band, link colour); text and card colours stay the same so every theme stays readable.
 *   logo: 'light' | 'dark'   which GrowSquare wordmark to use on the header
 */
export const THEMES = {
    default: {
        pageBg: '#F4F5F7', logo: 'light',
        headerBg: '#272B41', headerText: '#FFFFFF', headerMuted: '#ADB4D2', headerBorder: null,
        footerBg: '#272B41', footerText: '#ADB4D2', footerBorder: null,
        tableHeadBg: '#EBF7FF', tableHeadText: '#262626',
        totalBg: '#272B41', totalLabel: '#ADB4D2', totalValue: '#FFFFFF',
        accent: '#407FE9',
    },
    // White header: use this with dark logos.
    light: {
        pageBg: '#F4F5F7', logo: 'dark',
        headerBg: '#FFFFFF', headerText: '#272B41', headerMuted: '#848484', headerBorder: '#E3E8F0',
        footerBg: '#FFFFFF', footerText: '#848484', footerBorder: '#E3E8F0',
        tableHeadBg: '#EBF7FF', tableHeadText: '#262626',
        totalBg: '#407FE9', totalLabel: '#DCE8FF', totalValue: '#FFFFFF',
        accent: '#407FE9',
    },
    emerald: {
        pageBg: '#F3F7F5', logo: 'light',
        headerBg: '#0F3D2E', headerText: '#FFFFFF', headerMuted: '#9FD8BC', headerBorder: null,
        footerBg: '#0F3D2E', footerText: '#9FD8BC', footerBorder: null,
        tableHeadBg: '#E7F6EE', tableHeadText: '#1B3B2E',
        totalBg: '#0F3D2E', totalLabel: '#9FD8BC', totalValue: '#FFFFFF',
        accent: '#1F9D63',
    },
    violet: {
        pageBg: '#F5F4FA', logo: 'light',
        headerBg: '#2F2A6B', headerText: '#FFFFFF', headerMuted: '#C4BCFF', headerBorder: null,
        footerBg: '#2F2A6B', footerText: '#C4BCFF', footerBorder: null,
        tableHeadBg: '#F0EDFF', tableHeadText: '#2B2660',
        totalBg: '#2F2A6B', totalLabel: '#C4BCFF', totalValue: '#FFFFFF',
        accent: '#6A5AF9',
    },
    charcoal: {
        pageBg: '#F4F4F5', logo: 'light',
        headerBg: '#1F2328', headerText: '#FFFFFF', headerMuted: '#A0A7B4', headerBorder: null,
        footerBg: '#1F2328', footerText: '#A0A7B4', footerBorder: null,
        tableHeadBg: '#EEF0F2', tableHeadText: '#1F2328',
        totalBg: '#1F2328', totalLabel: '#A0A7B4', totalValue: '#FFFFFF',
        accent: '#BA1757',
    },
    // ---- more coloured-header themes ----
    ocean: {
        pageBg: '#F2F7F8', logo: 'light',
        headerBg: '#0B3C49', headerText: '#FFFFFF', headerMuted: '#9ED3DF', headerBorder: null,
        footerBg: '#0B3C49', footerText: '#9ED3DF', footerBorder: null,
        tableHeadBg: '#E4F4F7', tableHeadText: '#0B3C49',
        totalBg: '#0B3C49', totalLabel: '#9ED3DF', totalValue: '#FFFFFF',
        accent: '#0E8FA6',
    },
    crimson: {
        pageBg: '#F9F4F6', logo: 'light',
        headerBg: '#5A1030', headerText: '#FFFFFF', headerMuted: '#F0A8C4', headerBorder: null,
        footerBg: '#5A1030', footerText: '#F0A8C4', footerBorder: null,
        tableHeadBg: '#FCE8F0', tableHeadText: '#5A1030',
        totalBg: '#5A1030', totalLabel: '#F0A8C4', totalValue: '#FFFFFF',
        accent: '#BA1757',
    },
    amber: {
        pageBg: '#FAF7F1', logo: 'light',
        headerBg: '#4A3200', headerText: '#FFFFFF', headerMuted: '#E8CD94', headerBorder: null,
        footerBg: '#4A3200', footerText: '#E8CD94', footerBorder: null,
        tableHeadBg: '#FFF4DE', tableHeadText: '#4A3200',
        totalBg: '#4A3200', totalLabel: '#E8CD94', totalValue: '#FFFFFF',
        accent: '#D98A00',
    },
    slate: {
        pageBg: '#F3F5F8', logo: 'light',
        headerBg: '#334155', headerText: '#FFFFFF', headerMuted: '#A9B6C9', headerBorder: null,
        footerBg: '#334155', footerText: '#A9B6C9', footerBorder: null,
        tableHeadBg: '#EEF2F7', tableHeadText: '#27303F',
        totalBg: '#334155', totalLabel: '#A9B6C9', totalValue: '#FFFFFF',
        accent: '#4F6D9A',
    },
    royal: {
        pageBg: '#F3F6FB', logo: 'light',
        headerBg: '#12306B', headerText: '#FFFFFF', headerMuted: '#A9C1F0', headerBorder: null,
        footerBg: '#12306B', footerText: '#A9C1F0', footerBorder: null,
        tableHeadBg: '#E8EFFD', tableHeadText: '#12306B',
        totalBg: '#12306B', totalLabel: '#A9C1F0', totalValue: '#FFFFFF',
        accent: '#2F6FED',
    },
    sunset: {
        pageBg: '#FAF5F2', logo: 'light',
        headerBg: '#7A2E0E', headerText: '#FFFFFF', headerMuted: '#F5B592', headerBorder: null,
        footerBg: '#7A2E0E', footerText: '#F5B592', footerBorder: null,
        tableHeadBg: '#FFEDE3', tableHeadText: '#5E230B',
        totalBg: '#7A2E0E', totalLabel: '#F5B592', totalValue: '#FFFFFF',
        accent: '#E4572E',
    },
    // ---- more white-header themes (use with dark logos) ----
    lightEmerald: {
        pageBg: '#F4F5F7', logo: 'dark',
        headerBg: '#FFFFFF', headerText: '#272B41', headerMuted: '#848484', headerBorder: '#D5EBDD',
        footerBg: '#FFFFFF', footerText: '#848484', footerBorder: '#D5EBDD',
        tableHeadBg: '#E7F6EE', tableHeadText: '#1B3B2E',
        totalBg: '#1F9D63', totalLabel: '#FFFFFF', totalValue: '#FFFFFF',
        accent: '#1F9D63',
    },
    lightViolet: {
        pageBg: '#F4F5F7', logo: 'dark',
        headerBg: '#FFFFFF', headerText: '#272B41', headerMuted: '#848484', headerBorder: '#E1DCFA',
        footerBg: '#FFFFFF', footerText: '#848484', footerBorder: '#E1DCFA',
        tableHeadBg: '#F0EDFF', tableHeadText: '#2B2660',
        totalBg: '#6A5AF9', totalLabel: '#FFFFFF', totalValue: '#FFFFFF',
        accent: '#6A5AF9',
    },
    lightCrimson: {
        pageBg: '#F4F5F7', logo: 'dark',
        headerBg: '#FFFFFF', headerText: '#272B41', headerMuted: '#848484', headerBorder: '#F3D3E0',
        footerBg: '#FFFFFF', footerText: '#848484', footerBorder: '#F3D3E0',
        tableHeadBg: '#FCE8F0', tableHeadText: '#5A1030',
        totalBg: '#BA1757', totalLabel: '#FFFFFF', totalValue: '#FFFFFF',
        accent: '#BA1757',
    },
};

/** theme: a THEMES key, or an object that overrides tokens (optionally with `base`). */
export const resolveTheme = (theme) => {
    if (theme && typeof theme === 'object') {
        return { ...(THEMES[theme.base] || THEMES.default), ...theme };
    }
    return THEMES[theme] || THEMES.default;
};

const themedStylesCache = new Map();
const getThemedStyles = (t) => {
    const key = JSON.stringify(t);
    if (!themedStylesCache.has(key)) {
        themedStylesCache.set(key, StyleSheet.create({
            headerBand: {
                position: 'absolute', top: 0, left: 0, right: 0, height: HEADER_HEIGHT,
                backgroundColor: t.headerBg, paddingHorizontal: 36,
                flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
                ...(t.headerBorder ? { borderBottomWidth: 1, borderBottomColor: t.headerBorder, borderStyle: 'solid' } : {}),
            },
            headerTitle: { fontSize: 20, fontWeight: 'bold', color: t.headerText, textAlign: 'right', letterSpacing: 0.5 },
            headerNumber: { fontSize: 9.5, color: t.headerMuted, textAlign: 'right', marginTop: 2 },
            footerBand: {
                position: 'absolute', bottom: 0, left: 0, right: 0, height: FOOTER_HEIGHT,
                backgroundColor: t.footerBg, paddingHorizontal: 36,
                flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
                ...(t.footerBorder ? { borderTopWidth: 1, borderTopColor: t.footerBorder, borderStyle: 'solid' } : {}),
            },
            footerText: { fontSize: 8, color: t.footerText },
            tableHeader: {
                flexDirection: 'row', backgroundColor: t.tableHeadBg,
                borderTopLeftRadius: 3, borderTopRightRadius: 3,
            },
            tableHeadCell: {
                flex: 1, paddingVertical: 10, paddingHorizontal: 8, fontSize: 9, fontWeight: 'bold',
                color: t.tableHeadText, wordBreak: 'break-word', flexWrap: 'wrap', flexShrink: 1, overflow: 'hidden',
            },
            totalBand: {
                backgroundColor: t.totalBg, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
                paddingVertical: 12, paddingHorizontal: 16, borderBottomLeftRadius: 9, borderBottomRightRadius: 9,
            },
            totalBandLabel: { fontSize: 10, color: t.totalLabel },
            totalBandValue: { fontSize: 13, fontWeight: 'bold', color: t.totalValue },
        }));
    }
    return themedStylesCache.get(key);
};

Font.register({
    family: 'Noto Sans',
    fonts: [
        { src: '/fonts/noto-sans-v42-latin-regular.ttf' },
        { src: '/fonts/noto-sans-v42-latin-700.ttf', fontWeight: 'bold' }
    ],
});

Font.registerHyphenationCallback((word) => Array.from(word));

export const styles = StyleSheet.create({
    page: {
        fontFamily: 'Noto Sans',
        color: COLORS.text,
        fontSize: 9,
        paddingTop: HEADER_HEIGHT + 20,
        paddingBottom: FOOTER_HEIGHT + 20,
        paddingHorizontal: 36,
    },

    // ---- shell ----
    logo: { height: LOGO_HEIGHT, objectFit: 'contain' },
    logoText: { fontSize: 13, fontWeight: 'bold' },
    // ---- cards ----
    card: {
        backgroundColor: COLORS.white,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderStyle: 'solid',
        borderRadius: 10,
        paddingVertical: 14,
        paddingHorizontal: 16,
    },
    label: {
        fontSize: 7.5,
        fontWeight: 'bold',
        color: COLORS.label,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        marginBottom: 5,
    },
    metaRow: { flexDirection: 'row' },
    metaItem: { marginRight: 40 },
    metaValue: { fontSize: 10, fontWeight: 'bold', color: COLORS.text },
    partyRow: { flexDirection: 'row', marginTop: 14 },
    partyName: { fontSize: 10.5, fontWeight: 'bold', color: COLORS.heading, marginBottom: 4 },
    partyLine: { fontSize: 8.5, lineHeight: 1.45, color: COLORS.secondary },

    // ---- table ----
    tableCard: {
        marginTop: 14,
        backgroundColor: COLORS.white,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderStyle: 'solid',
        borderRadius: 4,
    },
    tableRow: { flexDirection: 'row' },
    tableRowDivider: { borderTopWidth: 1, borderTopColor: COLORS.divider, borderStyle: 'solid' },
    tableCell: {
        flex: 1,
        paddingVertical: 10,
        paddingHorizontal: 8,
        fontSize: 9.5,
        color: COLORS.text,
        wordBreak: 'break-word',
        flexWrap: 'wrap',
        flexShrink: 1,
        overflow: 'hidden',
    },
    rightAlign: { textAlign: 'right' },

    // ---- totals ----
    // Notes (left) and totals (right) sit side by side
    totalsRow: { flexDirection: 'row', alignItems: 'stretch', justifyContent: 'flex-end', marginTop: 14 },
    notesCard: { flex: 1, marginRight: 14 },
    totalCard: {
        width: 230,
        backgroundColor: COLORS.white,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderStyle: 'solid',
        borderRadius: 10,
    },
    // flexGrow keeps the navy Grand Total band at the bottom when the card is stretched to match the notes
    totalRows: { paddingVertical: 12, paddingHorizontal: 16, flexGrow: 1 },
    totalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 },
    totalLabel: { fontSize: 9.5, color: COLORS.secondary },
    totalValue: { fontSize: 9.5, fontWeight: 'bold', color: COLORS.text, textAlign: 'right' },
});

const TERMS_FONT_SIZE = 9;
export const termsHtmlStyle = {
    fontSize: TERMS_FONT_SIZE,
    color: COLORS.secondary,
    lineHeight: 1.45,
};
const termsHtmlStylesheet = {
    p: { marginVertical: 0, marginBottom: 4 },
    ul: { marginVertical: 0, marginBottom: 4 },
    ol: { marginVertical: 0, marginBottom: 4 },
    a: { textDecoration: 'underline' }, // colour comes from the theme accent
};
const ALLOWED_STYLE_PROPS = new Set([
    'color', 'background-color', 'font-weight', 'font-style', 'text-align', 'text-decoration',
    'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
    'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
]);
const toPt = (raw) => {
    const m = String(raw).trim().match(/^(-?\d*\.?\d+)(px|pt|rem|em)?$/i);
    if (!m) return null;
    const n = parseFloat(m[1]);
    const unit = (m[2] || 'pt').toLowerCase();
    if (unit === 'px') return `${+(n * 0.75).toFixed(2)}pt`;
    if (unit === 'rem' || unit === 'em') return `${+(n * TERMS_FONT_SIZE).toFixed(2)}pt`;
    return `${n}pt`;
};
const cleanInlineStyle = (styleText = '') => {
    const out = [];
    const push = (prop, value) => out.push(`${prop}: ${value}`);

    styleText.split(';').forEach((decl) => {
        const idx = decl.indexOf(':');
        if (idx === -1) return;
        const prop = decl.slice(0, idx).trim().toLowerCase();
        const value = decl.slice(idx + 1).trim();
        if (!prop || !value) return;

        if (prop === 'margin' || prop === 'padding') {
            const p = value.split(/\s+/).map(toPt);
            if (p.length > 4 || p.some((v) => v === null)) return;
            const [t, r = t, b = t, l = r] = p;
            push(`${prop}-top`, t);
            push(`${prop}-right`, r);
            push(`${prop}-bottom`, b);
            push(`${prop}-left`, l);
            return;
        }

        if (!ALLOWED_STYLE_PROPS.has(prop)) return;

        if (/^(margin|padding)-/.test(prop)) {
            const pt = toPt(value);
            if (pt !== null) push(prop, pt);
        } else if (prop === 'text-decoration') {
            const v = value.split(/\s+/)[0];
            if (['underline', 'line-through', 'none'].includes(v)) push(prop, v);
        } else if (prop === 'text-align') {
            if (['left', 'right', 'center', 'justify'].includes(value)) push(prop, value);
        } else {
            push(prop, value);
        }
    });
    return out.join('; ');
};
export const toSafeHtml = (value) => {
    if (!value) return '';
    const isHtml = /<\/?[a-z][\s\S]*>/i.test(value);
    const html = isHtml ? value : `<p>${String(value).replace(/\n/g, '<br/>')}</p>`;

    DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
        if (data.attrName === 'style') data.attrValue = cleanInlineStyle(data.attrValue);
    });
    try {
        return DOMPurify.sanitize(html);
    } finally {
        DOMPurify.removeHook('uponSanitizeAttribute');
    }
};

/** Address lines without "undefined" / empty fragments. */
export const joinParts = (parts, sep = ', ') => parts.filter((p) => p && String(p).trim() && String(p) !== '0').join(sep);

export const addressLines = (addr = {}) => [
    joinParts([addr?.street, addr?.city]),
    joinParts([addr?.state, addr?.zipcode]),
    addr?.country,
    addr?.email ? `Email: ${addr.email}` : null,
    addr?.mobile ? `Mobile: ${addr.mobile}` : null,
].filter(Boolean);

export const money = (value) =>
    Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const formatDate = (value, profile) =>
    value ? moment(value).format(profile?.dateFormate || 'YYYY-MM-DD') : '-';


/** Resolves the theme once per document: { t, ts, logo, htmlStylesheet } */
export const preparePdfTheme = (theme, logoSrc) => {
    const t = resolveTheme(theme);
    return {
        t,
        ts: getThemedStyles(t),
        logo: logoSrc || null,
        htmlStylesheet: { ...termsHtmlStylesheet, a: { ...termsHtmlStylesheet.a, color: t.accent } },
    };
};

/** Document + Page with the header band and footer band on every page. */
export const PdfDocument = ({ documentTitle, headline, number, p, companyData, children }) => {
    return (
        <Document title={documentTitle}>
            <Page size="A4" style={[styles.page, { backgroundColor: p.t.pageBg }]}>
                {/* Header band (every page) */}
                <View style={p.ts.headerBand} fixed>
                    {p.logo ? (
                        <Image style={[styles.logo, { width: LOGO_HEIGHT * (p.logoRatio || 1) }]} src={p.logo} />
                    ) : (
                        <Text style={[styles.logoText, { color: p.t.headerText }]}>
                            {companyData?.registeredName || companyData?.companyName || ''}
                        </Text>
                    )}
                    <View>
                        <Text style={p.ts.headerTitle}>{headline}</Text>
                        <Text style={p.ts.headerNumber}>#{number}</Text>
                    </View>
                </View>

                {/* Footer band (every page) */}
                <View style={p.ts.footerBand} fixed>
                    <Text style={p.ts.footerText}>{companyData?.accountUrl || ''}</Text>
                    <Text
                        style={p.ts.footerText}
                        render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
                    />
                </View>

                {children}
            </Page>
        </Document>
    );
};

/** items: [[label, value], ...]; falsy entries are skipped. */
export const MetaCard = ({ items }) => (
    <View style={styles.card} wrap={false}>
        <View style={styles.metaRow}>
            {items.filter(Boolean).map(([label, value]) => (
                <View key={label} style={styles.metaItem}>
                    <Text style={styles.label}>{label}</Text>
                    <Text style={styles.metaValue}>{value}</Text>
                </View>
            ))}
        </View>
    </View>
);

const PartyCard = ({ title, name, lines, style }) => (
    <View style={[styles.card, { flex: 1 }, style]} wrap={false}>
        <Text style={styles.label}>{title}</Text>
        {name ? <Text style={styles.partyName}>{name}</Text> : null}
        {lines.map((line, i) => (
            <Text key={i} style={styles.partyLine}>{line}</Text>
        ))}
    </View>
);

/** Billed from / Bill to (+ Ship to only when it differs from Bill to). */
export const PartiesRow = ({ companyData, data, accountJsonData }) => {
    const accountRef = accountJsonData?.registration_number || accountJsonData?.account_number;
    const withRef = (name) => joinParts([name, accountRef ? `(${accountRef})` : null], ' ');

    const fromName = companyData?.registeredName ? companyData?.registeredName : companyData?.companyName;
    const fromLines = [
        companyData?.registrationNumber ? companyData?.registrationNumber : companyData?.companyCode,
        joinParts([companyData?.street, companyData?.city]),
        joinParts([companyData?.state, companyData?.zipcode && companyData?.zipcode !== '0' ? companyData?.zipcode : null, companyData?.country]),
    ].filter(Boolean);

    const billLines = addressLines(data?.billingAddress);
    const shipLines = addressLines(data?.shippingAddress);
    const billName = withRef(data?.billingAddress?.name);
    const shipName = withRef(data?.shippingAddress?.name);
    const showShipTo = shipLines.length > 0 && (billName !== shipName || billLines.join('|') !== shipLines.join('|'));

    return (
        <View style={styles.partyRow}>
            <PartyCard title="Billed from" name={fromName} lines={fromLines} style={{ marginRight: 10 }} />
            <PartyCard
                title={showShipTo ? 'Bill to' : 'Bill to / Ship to'}
                name={billName}
                lines={billLines}
                style={showShipTo ? { marginRight: 10, paddingHorizontal: 12 } : undefined}
            />
            {showShipTo ? (
                <PartyCard title="Ship to" name={shipName} lines={shipLines} style={{ paddingHorizontal: 12 }} />
            ) : null}
        </View>
    );
};

/** Line items table. Handles hidden columns and custom mode (both off by default). */
export const LineItemsTable = ({ tableItem, tableColum, isCustomMode = false, currencySettings, profile, p }) => {
    const visibleColumns = tableColum?.filter((col) => !col.hidden) || [];
    return (
        <View style={styles.tableCard}>
            <View style={[p.ts.tableHeader, isCustomMode && { flexWrap: 'wrap' }]} wrap={false}>
                {visibleColumns.map((col, colIndex) => (
                    <Text
                        key={col.key}
                        style={[
                            p.ts.tableHeadCell,
                            { flex: isCustomMode ? (colIndex === 0 ? 2.0 : 1) : 1 },
                            colIndex === tableColum.length - 1 && styles.rightAlign
                        ]}
                    >
                        {isCustomMode
                            ? (col.type === "currency"
                                ? `${col.title} (${currencySettings?.currency})`
                                : col.title)
                            : typeof col.title === 'object'
                                ? col.title.props.children
                                : col.title}
                    </Text>
                ))}
            </View>
            {tableItem?.map((item, index) => (
                <View
                    key={index}
                    wrap={false}
                    style={[
                        styles.tableRow,
                        index > 0 && styles.tableRowDivider,
                        isCustomMode && { flexWrap: 'wrap' },
                    ]}
                >
                    {visibleColumns.map((col, colIndex) => (
                        <Text
                            key={isCustomMode ? col.id : col.key}
                            style={[
                                styles.tableCell,
                                { flex: isCustomMode ? (colIndex === 0 ? 2.0 : 1) : 1 },
                                colIndex === tableColum.length - 1 && styles.rightAlign
                            ]}
                        >
                            {col.key === "duration"
                                ? item[col.key]
                                    ? item.productCategory !== "Software"
                                        ? "One off"
                                        : `${item[col.key]} Month(s)`
                                    : "-"
                                : col.type === "date"
                                    ? item[isCustomMode ? col.id : col.key]
                                        ? moment(item[isCustomMode ? col.id : col.key]).format(profile.dateFormate)
                                        : "-"
                                    : ["total", "price", "amount", "subtotal"].includes(isCustomMode ? col.id : col.key)
                                        ? money(item[isCustomMode ? col.id : col.key])
                                        : item[isCustomMode ? col.id : col.key] || "-"}
                        </Text>
                    ))}
                </View>
            ))}
        </View>
    );
};

/** Fixed-width totals card with the Grand Total band. */
export const TotalsCard = ({ Calculation, currencySettings, p }) => (
    <View style={styles.totalCard}>
        <View style={styles.totalRows}>
            <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Subtotal</Text>
                <Text style={styles.totalValue}>{currencySettings?.currency} {money(Calculation?.total)}</Text>
            </View>
            {Number(Calculation?.dealDiscount) !== 0 && (
                <View style={styles.totalRow}>
                    <Text style={styles.totalLabel}>Discount</Text>
                    <Text style={styles.totalValue}>{Calculation?.dealDiscount} %</Text>
                </View>
            )}
            {Number(Calculation?.tax) !== 0 && (
                <View style={styles.totalRow}>
                    <Text style={styles.totalLabel}>Tax</Text>
                    <Text style={styles.totalValue}>{Calculation?.tax} %</Text>
                </View>
            )}
            {Number(Calculation?.adjustment) !== 0 && (
                <View style={styles.totalRow}>
                    <Text style={styles.totalLabel}>Adjustment</Text>
                    <Text style={styles.totalValue}>
                        {currencySettings?.currency}{" "}
                        {money(Calculation?.adjustment)}
                    </Text>
                </View>
            )}
            {Number(Calculation?.shippingss) !== 0 && (
                <View style={styles.totalRow}>
                    <Text style={styles.totalLabel}>Shipping/Handling</Text>
                    <Text style={styles.totalValue}>
                        {currencySettings?.currency}{" "}
                        {money(Calculation?.shippingss)}
                    </Text>
                </View>
            )}
        </View>
        <View style={p.ts.totalBand}>
            <Text style={p.ts.totalBandLabel}>Grand Total</Text>
            <Text style={p.ts.totalBandValue}>{currencySettings?.currency} {money(Calculation?.grandTotal)}</Text>
        </View>
    </View>
);

/** Card with a small uppercase title and sanitised HTML content (notes, terms and conditions). */
export const InfoCard = ({ title, html, p, style }) => (
    <View style={[styles.card, style]}>
        <Text style={styles.label}>{title}</Text>
        <Html style={termsHtmlStyle} stylesheet={p.htmlStylesheet}>
            {toSafeHtml(html)}
        </Html>
    </View>
);